import React, { useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { MutableRefObject } from 'react';
import { StepProp } from '../../../../components/Step';
import { TicketModalContext } from './TicketModalContext';
import ExpandModal from '../../../../components/ExpandModal';
import { Form as CustomForm } from '../../../../../../components/form7/Form';
import { Modal } from 'react-bootstrap';
import { Footer } from './Footer';
import { StepForm } from './StepForm';
import { FormItem, StepNamesType, TicketFormValues } from './types';
import { useAddTicketHeaderInfo } from '../hooks/useAddTicketHeaderInfo';
import { TicketPolicy } from '../types';

type Props = {
  formList: FormItem[];
  steps: StepProp<StepNamesType>[];
  visible: boolean;
  children?: React.ReactElement;
  onClose: () => void;
  onSubmit: (data: TicketFormValues) => void;
  isSubmitting: boolean;
  ticketPolicy: TicketPolicy | null;
};

export const BaseNewTicketModal: React.FC<React.PropsWithChildren<Props>> = ({
  onClose,
  steps,
  formList,
  visible,
  onSubmit,
  isSubmitting,
  ticketPolicy,
}) => {
  const [data, setData] = useState<TicketFormValues | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const getCurrentFormValues = useRef<GetCurrentFormValues>(() => ({}));
  // TODO if the expand modal functionality will be needed, this should be changed to use state
  const isExpanded = false;
  const maxStep = formList?.length || 0;

  const onCancel = () => {
    if (isExpanded || currentStep === 0) {
      onClose();
      return;
    }
    setCurrentStep(Math.max(currentStep - 1, 0));
  };

  const formsList = useMemo(
    () => formList.map((item) => item.form),
    [formList],
  );

  const getValidationScheme = () => {
    if (!isExpanded) {
      return formList[currentStep].validationScheme;
    }

    return formList.reduce((schema, formItem) => {
      if (!formItem.validationScheme) {
        return schema;
      }
      return schema
        ? schema.concat(formItem.validationScheme)
        : formItem.validationScheme;
    }, formList[0].validationScheme);
  };

  const onNext = (formData: TicketFormValues) => {
    const newData = { ...data, ...formData };
    setData((prevState) => {
      return { ...prevState, ...newData };
    });
    if (isExpanded || currentStep >= maxStep - 1) {
      onSubmit(newData);
      return;
    }
    setCurrentStep(Math.min(currentStep + 1, formList?.length - 1 || 0));
  };

  const renderBody = () => {
    if (isExpanded) {
      return;
    }
    return <StepForm list={formsList} />;
  };
  const onChange = (formData: Partial<TicketFormValues>) => {
    setData((prevState) => ({
      ...prevState,
      ...getCurrentFormValues.current(),
      ...formData,
    }));
  };
  const providerProps = {
    value: {
      steps,
      isExpanded,
      maxStep,
      currentStep,
      onCancel,
      onChange,
      onChangeStep: (step: number) => setCurrentStep(step),
      value: data,
      policy: ticketPolicy,
    },
  };

  const { title, description } = useAddTicketHeaderInfo({
    stepName: steps[currentStep].stepName,
    categoryName: data?.preview?.category ?? '',
  });

  return (
    <TicketModalContext.Provider {...providerProps}>
      <ExpandModal
        title={title}
        description={description}
        isExpanded={isExpanded}
        hasExpand={false}
        visible={visible}
        onClose={onClose}>
        <CustomForm
          shouldUnregister
          values={data ?? undefined}
          validationSchema={getValidationScheme()}
          onSubmit={onNext}>
          <CurrentFormValues getCurrentFormValues={getCurrentFormValues} />
          <Modal.Body className="p-0">{renderBody()}</Modal.Body>
          <Modal.Footer className="border-top-0">
            <Footer
              isSubmitting={isSubmitting}
              isExpanded={isExpanded}></Footer>
          </Modal.Footer>
        </CustomForm>
      </ExpandModal>
    </TicketModalContext.Provider>
  );
};

type CurrentFormValuesProps = {
  getCurrentFormValues: MutableRefObject<GetCurrentFormValues>;
};

type GetCurrentFormValues = () => TicketFormValues;

const CurrentFormValues = ({
  getCurrentFormValues,
}: CurrentFormValuesProps) => {
  const { getValues } = useFormContext<TicketFormValues>();
  getCurrentFormValues.current = getValues;
  return null;
};
